import React, { useState, useRef } from 'react';
import { TrashIcon, LinkIcon, ShoppingCartIcon, EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import api from '../../../middleware/api';
import PortalModal from '../../Common/Modal/PortalModal';
import MergeConfigurationsModal from './MergeConfigurationsModal';
import SendMailConfirmationModal from './SendMailConfirmationModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

const SelectionActions = ({ selectedCapsules, setSelectedCapsules, setData, capsules, customer }) => {
    const location = useLocation();
    const { setCapsule } = useOutletContext()
    const [selectedConfigurations, setSelectedConfigurations] = useState({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const modalRef = useRef(null);
    const modalConfirmationDeleteRef = useRef(null);
    const infoModalRef = useRef(null);
    const sendMailModalRef = useRef(null);
    const navigate = useNavigate()


    const confirmDelete = async () => {
        if (selectedCapsules.length === 0) {
            toast.error('Aucune capsule sélectionnée pour suppression.');
            return;
        }

        try {
            const response = await api.post('/capsules/delete/multiple', { capsuleIds: selectedCapsules });
            if (response.status === 200) {
                setData((prev) => ({
                    ...prev,
                    capsules: prev.capsules.filter(capsule => !selectedCapsules.includes(capsule.id))
                }));
                setSelectedCapsules([]);
                toast.success('Capsules supprimées avec succès.');
            }
        } catch (e) {
            toast.error('Erreur lors de la suppression des capsules. Veuillez réessayer.');
        }
        setShowDeleteConfirmation(false);
        modalConfirmationDeleteRef.current.hideModal();
    };

    const handleDeleteMultipleCapsules = () => {
        if (selectedCapsules.length === 0) {
            toast.error('Aucune capsule sélectionnée pour suppression.');
            return;
        }
        setShowDeleteConfirmation(true);
        modalConfirmationDeleteRef.current.showModal();
    };

    const handleMergeConfigurations = (resetSensibilities) => async () => {

        // Find the first configuration of each capsule
        const configurationIds = capsules.filter(capsule => selectedCapsules.includes(capsule.id)).map(capsule => capsule.configurations[0].id);

        try {
            const response = await api.post('/capsules/configurations/merge', {
                customerId: customer.id,
                configurationIds,
                resetSensibilities,

            });

            if (response.status === 200 && response.data) {
                setData((prev) => ({
                    ...prev,
                    capsules: [...prev.capsules, response.data],
                }));
                setSelectedCapsules([]);
                setSelectedConfigurations({});
                toast.success('Configurations fusionnées avec succès.');
                navigate(`./capsule/${response.data.id}`);
            } else {
                toast.error('Erreur lors de la fusion des configurations. Veuillez réessayer.');
            }
        } catch (e) {
            toast.error('Erreur lors de la fusion des configurations. Veuillez réessayer.');
        }
    };

    const handleConfigurationSelect = (capsuleId, configId) => {
        setSelectedConfigurations((prev) => {
            const currentSelections = prev[capsuleId] || [];
            const newSelections = currentSelections.includes(configId)
                ? currentSelections.filter(id => id !== configId)
                : [...currentSelections, configId];
            return {
                ...prev,
                [capsuleId]: newSelections,
            };
        });
    };

    const showModal = () => {
        if (modalRef.current) {
            modalRef.current.showModal();
        }
    };

    const hideModal = () => {
        if (modalRef.current) {
            modalRef.current.hideModal();
        }
    };

    const showInfoModal = () => {
        if (infoModalRef.current) {
            infoModalRef.current.showModal();
        }
    };

    const showSendMailModal = () => {
        if (sendMailModalRef.current) {
            sendMailModalRef.current.showModal();
        }
    };

    const hideSendMailModal = () => {
        if (sendMailModalRef.current) {
            sendMailModalRef.current.hideModal();
        }
    };

    const handleNavigateToOrderMultipleCapsules = () => {
        // If location pathname include 'capsule' remove it
        if (location.pathname.includes('capsule')) {
            navigate(`../commander`, { state: { capsuleIds: selectedCapsules } });
        } else {
            navigate(`./commander`, { state: { capsuleIds: selectedCapsules } });
        }
    }

    const handleSendSummary = async (email, customHtml) => {
        try {
            const response = await api.post('/capsules/send-resume-mail', { capsuleIds: selectedCapsules, email, customHtml: customHtml });
            if (response.data.success) {
                toast.success('Résumé envoyé avec succès.');
                setSelectedCapsules([]);
                setData((prev) => ({
                    ...prev,
                    capsules: prev.capsules.map((c) => {
                        if (selectedCapsules.includes(c.id)) {
                            return { ...c, hasSentReviewEmail: true }
                        }
                        return c
                    })
                }));
            } else {
                toast.error('Erreur lors de l\'envoi du résumé. Veuillez réessayer.');
            }
        } catch (e) {
            toast.error('Erreur lors de l\'envoi du résumé. Veuillez réessayer.');
        }
    }

    const hideDeleteModal = () => {
        if (modalConfirmationDeleteRef.current) {
            modalConfirmationDeleteRef.current.hideModal();
            setShowDeleteConfirmation(false);
        }
    };

    return (
        <div
            className="fixed bottom-0 left-0 right-0 bg-white w-fit m-auto border-t border-gray-200 shadow-2xl z-[9999] p-4 rounded-lg"
            role="toolbar"
            aria-label="Actions pour les capsules sélectionnées"
        >
            <div className="max-w-7xl mx-auto flex justify-center gap-4">
                <button
                    onClick={handleNavigateToOrderMultipleCapsules}
                    className="btn btn-sm btn-primary gap-2"
                    aria-label="Commander les capsules sélectionnées"
                >
                    <ShoppingCartIcon className="w-5 h-5" />
                    <span>Commander</span>
                </button>
                <button
                    onClick={showSendMailModal}
                    className="btn btn-sm btn-accent gap-2"
                    aria-label="Envoyer le résumé des capsules sélectionnées"
                >
                    <EnvelopeIcon className="w-5 h-5" />
                    <span>Envoyer le résumé</span>
                </button>
                <button
                    onClick={showModal}
                    className="btn btn-sm btn-warning gap-2"
                    aria-label="Fusionner les configurations"
                >
                    <LinkIcon className="w-5 h-5" />
                    <span>Fusionner</span>
                </button>

                <button
                    onClick={handleDeleteMultipleCapsules}
                    className="btn btn-sm btn-error gap-2"
                    aria-label="Supprimer les capsules sélectionnées"
                >
                    <TrashIcon className="w-5 h-5" />
                    <span>Supprimer</span>
                </button>

            </div>

            <MergeConfigurationsModal
                ref={modalRef}
                capsules={capsules}
                selectedCapsules={selectedCapsules}
                selectedConfigurations={selectedConfigurations}
                handleConfigurationSelect={handleConfigurationSelect}
                handleMergeConfigurations={handleMergeConfigurations}
                hideModal={hideModal}
                showInfoModal={showInfoModal}
            />

            <SendMailConfirmationModal
                ref={sendMailModalRef}
                initialEmail={customer?.email || ''}
                onSendMail={handleSendSummary}
                hideModal={hideSendMailModal}
            />

            <DeleteConfirmationModal
                ref={modalConfirmationDeleteRef}
                onConfirm={confirmDelete}
                hideModal={hideDeleteModal}
            />
        </div>
    );
};

export default SelectionActions;
